import { MinusIcon } from "@/components/icons/minus-icon";
import { PlusIcon } from "@/components/icons/plus-icon";
import { cn } from "@/utils/cn";
import { memo, useCallback } from "react";

interface CounterProps {
  /**
   * Current value
   */
  value: number;
  /**
   * Callback when value changes
   */
  onChange: (value: number) => void;
  /**
   * Minimum allowed value (default: 0)
   */
  min?: number;
  /**
   * Maximum allowed value (optional)
   */
  max?: number;
  /**
   * Whether the counter is disabled
   */
  disabled?: boolean;
  /**
   * Additional class names for the container
   */
  className?: string;

  color?: "white" | "green" | "purple" | "dark";
}

/**
 * Counter component with increment/decrement buttons
 * Used for numeric input with optional min/max constraints
 */
export const Counter = memo(function Counter({
  value,
  onChange,
  min = 0,
  max,
  disabled = false,
  className,
  color = "white",
}: CounterProps) {
  const canDecrement = !disabled && value > min;
  const canIncrement = !disabled && (max === undefined || value < max);
  // The dark variant sits on a near-black pill, so its glyphs need to be white.
  const iconClassName = color === "dark" ? "fill-white" : "fill-black";

  const getButtonClassName = useCallback(
    (enabled: boolean) => {
      if (color === "purple") {
        return {
          "bg-[#E0CDFBCC] hover:bg-[#E0CDFBCC]/50 cursor-pointer": enabled,
          "bg-[#E0CDFBCC]/30": !enabled,
        };
      }
      if (color === "white") {
        return {
          "bg-[#ffffff] hover:bg-label-tertiary/50 cursor-pointer": enabled,
          "bg-[#ffffff]/50": !enabled,
        };
      }

      if (color === "green") {
        return {
          "bg-[var(--color-green-200)] hover:bg-[var(--color-green-200)]/50 cursor-pointer":
            enabled,
          "bg-[var(--color-green-200)]/30": !enabled,
        };
      }

      if (color === "dark") {
        return {
          "bg-[#181d27] hover:bg-[#181d27]/80 cursor-pointer": enabled,
          "bg-[#181d27]/40": !enabled,
        };
      }

      return {
        "bg-black/10 hover:bg-black/50 cursor-pointer": enabled,
        "bg-white/10": !enabled,
      };
    },
    [color],
  );

  const handleDecrement = useCallback(() => {
    if (canDecrement) {
      onChange(value - 1);
    }
  }, [canDecrement, onChange, value]);

  const handleIncrement = useCallback(() => {
    if (canIncrement) {
      onChange(value + 1);
    }
  }, [canIncrement, onChange, value]);

  return (
    <div className={cn("flex w-23 items-center justify-between overflow-visible", className)}>
      <button
        onClick={handleDecrement}
        disabled={!canDecrement}
        className={cn("flex h-11 w-11.5 items-center justify-center rounded-l-full transition-all")}
        aria-label="Decrease value"
      >
        <div
          className={cn(
            "flex h-11 w-full items-center justify-center overflow-clip rounded-l-full",
            getButtonClassName(canDecrement),
          )}
        >
          <MinusIcon className={iconClassName} />
        </div>
      </button>

      <button
        onClick={handleIncrement}
        disabled={!canIncrement}
        className={cn("flex h-11 w-11.5 items-center justify-center transition-all")}
        aria-label="Increase value"
      >
        <div
          className={cn(
            "flex h-11 w-full items-center justify-center overflow-clip rounded-r-full",
            getButtonClassName(canIncrement),
          )}
        >
          <PlusIcon className={iconClassName} />
        </div>
      </button>
    </div>
  );
});
